import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

const dashboardPaths = {
  superadmin: "/superadmin/properties",
  property: "/propertyownerdashboard/properties",
  nonprofit: "/nonprofit/MyPropertyServices",
  realestate: "/realestatedashboard/properties",
};

const getCurrentDashboard = (pathname) => {
  const dashboard = Object.keys(dashboardPaths).find((key) =>
    pathname.includes(key)
  );
  return dashboard || "superadmin"; // Default to superadmin if no match found
};

const PostForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    propertyDetails: {
      title: "",
      listingID: "",
      address: "",
      price: "",
      propertyType: "",
      status: "",
      numberOfBedrooms: "",
      numberOfBathrooms: "",
      squareFootage: "",
      yearBuiltOrRemodeled: "",
      parking: "",
      utilities: "",
      heatingAndCooling: "",
      extraRooms: "",
      Appliances: "",
      outdoorAreas: "",
      amenities: "",
      wheelchairRamp: false,
      description: "",
    },
    location: {
      unitTitle: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      bedrooms: "",
      baths: "",
      managedBy: "",
      amenities: "",
    },
    neighborhood: {
      facts: "",
      petsAllowed: "",
      smokingAllowed: "",
    },
  });

  const { formData: initialData = {}, formId, formType } = location.state || {};
  const [uploadFiles, setUploadFiles] = useState(initialData.uploadFiles || []);
  const [removedFiles, setRemovedFiles] = useState([]);

  const handleInputChange = (e) => {
    const { name, value, dataset } = e.target;
    const section = dataset.section; // Retrieve the section from the data attribute

    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: value,
      },
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles((prevFiles) => [...prevFiles, ...files]);
  };
  const handleRemoveFile = (index) => {
    const fileToRemove = uploadFiles[index];
    if (typeof fileToRemove === "string") {
      // If the file is a URL (existing file), add it to removedFiles
      setRemovedFiles((prev) => [...prev, fileToRemove]);
    }
    setUploadFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // handel submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("Authentication token is missing. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Step 1: Upload ONLY NEW files (ignore existing URLs)
      const newFiles = uploadFiles.filter((file) => file instanceof File);
      let filesURL = [];

      if (newFiles.length > 0) {
        const formDataForFiles = new FormData();
        newFiles.forEach((file) =>
          formDataForFiles.append("uploadFiles", file)
        );

        if (newFiles.length === 1) {
          formDataForFiles.append("uploadFiles[]", newFiles[0]); // Note array syntax
        } else {
          newFiles.forEach((file, index) => {
            formDataForFiles.append(`uploadFiles[${index}]`, file);
          });
        }

        formDataForFiles.append("token", `"${token}"`);
        formDataForFiles.append(
          "foldername",
          "Service Provider | Transport Form"
        );

        const uploadResponse = await axios.post(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/upload-file",
          formDataForFiles,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (!uploadResponse.data.status) {
          throw new Error(uploadResponse.data.message || "File upload failed.");
        }

        filesURL = [...filesURL, ...uploadResponse.data.filesURL];
      }

      // Step 2: Prepare and send the form payload
      const payload = {
        postServiceFormData: {
          propertyDetails: { ...formData.propertyDetails },
          location: { ...formData.location },
          neighborhood: { ...formData.neighborhood },
          uploadFiles: filesURL,
          removedFiles,
        },
        token,
      };

      let response;

      if (formId) {
        // Update existing service
        payload.form_id = formId;
        response = await axios.put(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/postservice-form/update",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        // Create new service
        response = await axios.post(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/postservice-form/submit",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      if (response.status === 200) {
        toast.success(
          formId
            ? "Property form updated successfully!"
            : "Property form submitted successfully!"
        );
        // Reset form fields
        setFormData({
          propertyDetails: {
            title: "",
            listingID: "",
            address: "",
            price: "",
            propertyType: "",
            status: "",
            numberOfBedrooms: "",
            numberOfBathrooms: "",
            squareFootage: "",
            yearBuiltOrRemodeled: "",
            parking: "",
            utilities: "",
            heatingAndCooling: "",
            extraRooms: "",
            appliances: "",
            outdoorAreas: "",
            amenities: "",
            wheelchairRamp: false,
            description: "",
          },
          location: {
            unitTitle: "",
            address: "",
            city: "",
            state: "",
            zip: "",
            bedrooms: "",
            baths: "",
            managedBy: "",
            amenities: "",
          },
          neighborhood: {
            facts: "",
            petsAllowed: "",
            smokingAllowed: "",
          },
        });
        setUploadFiles([]);
        setRemovedFiles([]);
      } else {
        throw new Error(response.data.error || "Failed to submit the form.");
      }
    } catch (error) {
      console.error(
        "Error during form submission:",
        error.response?.data || error.message
      );
      toast.error(
        error.response?.data?.message ||
          "An unexpected error occurred. Check the console for details."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isLoading, setIsLoading] = useState(true);

  // edit feature
  useEffect(() => {
    if (formType === "Property Form" && formId) {
      fetchFormData(formId);
    } else if (
      initialData?.propertyDetails &&
      initialData?.location &&
      initialData?.neighborhood
    ) {
      setFormData(initialData);
      setUploadFiles([]);
    }
  }, [formId, formType, initialData]);

  const fetchFormData = async (formId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/postservice-form/get",
        { form_id: formId, token }
      );

      const formDataResponse = response.data?.form_data?.find(
        (item) => item.id === formId
      );
      // console.log(formDataResponse);

      if (formDataResponse) {
        setFormData({
          propertyDetails: formDataResponse.details || {},
          location: formDataResponse.location || {},
          neighborhood: formDataResponse.neighborhood || {},
        });
        setUploadFiles(formDataResponse.uploadFiles || []);
      } else {
        toast.error("Form data not found.");
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      toast.error("Unable to load form data.");
    } finally {
      setIsLoading(false);
    }
  };

  // breadcrumbs
  const handleBreadcrumbClick = () => {
    const currentDashboard = getCurrentDashboard(location.pathname);
    const path = dashboardPaths[currentDashboard];
    navigate(path);
  };

  return (
    <div className="bg-white min-h-screen w-full lg:w-[80vw] p-2 lg:p-6">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Form Section */}
      <div className=" bg-white  lg:px-6">
        {/* Breadcrumbs */}
        <div className="mb-4 text-sm">
          <button
            onClick={handleBreadcrumbClick}
            className="text-gray-500 hover:text-gray-700"
          >
            post a service / property
          </button>
          <span className="mx-2 text-gray-400">&gt;</span>
          <span className="text-purple-600 font-medium">Property</span>
        </div>
        <h2 className="text-2xl flex items-center font-bold mb-4 text-gray-800">
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2"
          >
            <path
              d="M13 25L4.95001 25L4.95001 3L22.95 3L22.95 14"
              stroke="#004A61"
              strokeWidth="2.4"
              strokeLinecap="square"
            />
            <path
              d="M9.00354 9L18.9565 9"
              stroke="#004A61"
              strokeWidth="2.4"
              strokeLinecap="square"
            />
            <path
              d="M9.00354 14L18.9565 14"
              stroke="#004A61"
              strokeWidth="2.4"
              strokeLinecap="square"
            />
            <path
              d="M9 19H13"
              stroke="#004A61"
              strokeWidth="2.4"
              strokeLinecap="square"
            />
            <path
              d="M25 20V25H17L17 20L21 17L25 20Z"
              stroke="#004A61"
              strokeWidth="2.4"
              strokeLinecap="square"
            />
            <path
              d="M21 23V25"
              stroke="#004A61"
              strokeWidth="2.4"
              strokeLinecap="square"
            />
          </svg>
          Post a Service or Property
        </h2>

        {/* Property Form Fields */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Property Title
              </label>
              <input
                type="text"
                placeholder="Enter title"
                name="title"
                data-section="propertyDetails"
                value={formData.propertyDetails.title}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Listing Id
              </label>
              <input
                type="text"
                placeholder="Add ListingId"
                name="listingID"
                data-section="propertyDetails"
                value={formData.propertyDetails.listingID}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Company Address
              </label>
              <input
                type="text"
                placeholder="Enter Company Address"
                name="address"
                data-section="propertyDetails"
                value={formData.propertyDetails.address}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Price
              </label>
              <input
                type="text"
                placeholder="Enter Price"
                name="price"
                data-section="propertyDetails"
                value={formData.propertyDetails.price}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Property Type
              </label>
              <input
                type="text"
                placeholder="Enter Property Type"
                name="propertyType"
                data-section="propertyDetails"
                value={formData.propertyDetails.propertyType}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Status
              </label>
              <input
                type="text"
                placeholder="Enter Status"
                name="status"
                data-section="propertyDetails"
                value={formData.propertyDetails.status}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Number of Bedrooms
              </label>
              <input
                type="text"
                placeholder="Enter Number of Bedrooms"
                name="numberOfBedrooms"
                data-section="propertyDetails"
                value={formData.propertyDetails.numberOfBedrooms}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Number of Bathrooms
              </label>
              <input
                type="text"
                data-section="propertyDetails"
                placeholder="Enter Number of Bathrooms"
                name="numberOfBathrooms"
                value={formData.propertyDetails.numberOfBathrooms}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Square Footage
              </label>
              <input
                type="text"
                placeholder="Enter Square Footage"
                name="squareFootage"
                data-section="propertyDetails"
                value={formData.propertyDetails.squareFootage}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Year Built/Remodeled
              </label>
              <input
                type="text"
                placeholder="Enter Square Footage"
                name="yearBuiltOrRemodeled"
                data-section="propertyDetails"
                value={formData.propertyDetails.yearBuiltOrRemodeled}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Parking
              </label>
              <input
                type="text"
                placeholder="Enter Parking"
                name="parking"
                data-section="propertyDetails"
                value={formData.propertyDetails.parking}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Utilities
              </label>
              <input
                type="text"
                placeholder="Enter Utilities"
                name="utilities"
                data-section="propertyDetails"
                value={formData.propertyDetails.utilities}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Heating and Cooling Type
              </label>
              <input
                type="text"
                placeholder="Enter Heating and Cooling Type"
                name="heatingAndCooling"
                data-section="propertyDetails"
                value={formData.propertyDetails.heatingAndCooling}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Extra Room /Features
              </label>
              <input
                type="text"
                placeholder="Enter Extra Features"
                name="extraRooms"
                data-section="propertyDetails"
                value={formData.propertyDetails.extraRooms}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Appliances
              </label>
              <input
                type="text"
                placeholder="Enter Appliances"
                name="Appliances"
                data-section="propertyDetails"
                value={formData.propertyDetails.Appliances}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Outdooor Areas
              </label>
              <input
                type="text"
                placeholder="Enter Appliances"
                name="outdoorAreas"
                data-section="propertyDetails"
                value={formData.propertyDetails.outdoorAreas}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Amenities
              </label>
              <input
                type="text"
                placeholder="Enter Amenities"
                name="amenities"
                data-section="propertyDetails"
                value={formData.propertyDetails.amenities}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Wheel chair ramp
              </label>
              <input
                type="text"
                placeholder="Enter Amenities"
                name="wheelchairRamp"
                data-section="propertyDetails"
                value={formData.propertyDetails.wheelchairRamp}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Unit Title
              </label>
              <input
                type="text"
                placeholder="Enter Unit Title"
                name="unitTitle"
                data-section="location"
                value={formData.location.unitTitle}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            {/* <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Address
              </label>
              <input
                type="text"
                placeholder="Enter Address"
                data-section="location"
                name="locationAddress"
                value={formData.location.address}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div> */}
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                City
              </label>
              <input
                type="text"
                placeholder="Enter City"
                data-section="location"
                name="city"
                value={formData.location.city}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                State
              </label>
              <input
                type="text"
                placeholder="Enter State"
                data-section="location"
                name="state"
                value={formData.location.state}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Bedrooms
              </label>
              <input
                type="text"
                placeholder="Enter Bedrooms"
                data-section="location"
                name="bedrooms"
                value={formData.location.bedrooms}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Managed By
              </label>
              <input
                type="text"
                placeholder="Managed By ?"
                data-section="location"
                name="managedBy"
                value={formData.location.managedBy}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Zip
              </label>
              <input
                type="text"
                placeholder="Enter Zip"
                data-section="location"
                name="zip"
                value={formData.location.zip}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            {/* <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Amenities
              </label>
              <input
                type="text"
                placeholder="Enter Amenities"
                data-section="location"
                name="locationAmenities"
                value={formData.location.amenities}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div> */}
          </div>
          <div>
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2 mt-2">
                Extensive Description
              </label>
              <input
                type="text"
                name="description"
                value={formData.propertyDetails.description}
                onChange={handleInputChange}
                data-section="propertyDetails"
                placeholder="Enter Title"
                className="w-[60vw] border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Map Section */}
          <div className="p-8 mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Property Location
            </h3>
            <div className="h-64 bg-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">Map Placeholder</p>
            </div>
          </div>

          {/* Neighbor Fact, Pet Allowed, and Bath Fields */}
          <div className="grid grid-cols-1 md:flex flex-row space-x-6">
            <div className="mb-4 md:mb-0">
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Neighbor Fact
              </label>
              <input
                type="text"
                name="facts"
                data-section="neighborhood"
                value={formData.neighborhood.facts}
                onChange={handleInputChange}
                placeholder="Enter Fact"
                className="w-full md:w-[15vw] border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div className="mb-4 md:mb-0">
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Pets Allowed
              </label>
              <input
                type="text"
                name="petsAllowed"
                data-section="neighborhood"
                value={formData.neighborhood.petsAllowed}
                onChange={handleInputChange}
                placeholder="Enter Pets Allowed"
                className="w-full md:w-[15vw] border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div className="mb-4 md:mb-0">
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Bath
              </label>
              <input
                type="text"
                name="baths"
                data-section="location"
                value={formData.location.baths}
                onChange={handleInputChange}
                placeholder="Bath"
                className="w-full md:w-[15vw] border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
            <div className="mb-4 md:mb-0">
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Smooking Allowed
              </label>
              <input
                type="text"
                name="smokingAllowed"
                data-section="neighborhood"
                value={formData.neighborhood.smokingAllowed}
                onChange={handleInputChange}
                placeholder="smoking allowed"
                className="w-full md:w-[15vw] border border-gray-300 rounded-lg p-2 focus:border-blue-500"
              />
            </div>
          </div>
          {/* File Upload Section */}
          <div className="p-8 mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Upload Files
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["Floor Plans", "Pics", "Virtual Tour"].map((item, idx) => (
                <div
                  key={idx}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center"
                >
                  <input
                    type="file"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id={`fileInput-${idx}`}
                  />
                  <label
                    htmlFor={`fileInput-${idx}`}
                    className="cursor-pointer"
                  >
                    <div className="ml-0 lg:ml-14">
                      <svg
                        width="80"
                        height="80"
                        viewBox="0 0 80 80"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M40 63.635L40 39.945V44.065"
                          stroke="#6F7787"
                          strokeWidth="2.472"
                          strokeMiterlimit="10"
                          strokeLinecap="square"
                        />
                        <path
                          d="M28.67 51.305L40 39.975L51.33 51.305"
                          stroke="#6F7787"
                          strokeWidth="2.472"
                          strokeMiterlimit="10"
                          strokeLinecap="square"
                        />
                        <path
                          d="M48.2397 63.6055C61.1895 62.5253 71.092 51.6031 70.9021 38.6098C70.7121 25.6164 60.4946 14.9884 47.5188 14.2871C34.5429 13.5859 23.2395 23.0508 21.6503 35.948C14.2778 36.6796 8.77369 43.0632 9.13497 50.4631C9.49626 57.863 15.596 63.68 23.0047 63.69L31.7597 63.69"
                          stroke="#6F7787"
                          strokeWidth="2.472"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-500">Drop files here</p>
                    <h4 className="text-slate-500">{item}</h4>
                    <div className="mt-4">
                      <p className="text-sm text-gray-600">
                        {uploadFiles.length > 0
                          ? `${uploadFiles.length} file(s) selected`
                          : "No files selected"}
                      </p>
                    </div>
                  </label>
                </div>
              ))}
            </div>

            {/* Preview Uploaded Images */}
            <div className="mt-6 flex flex-wrap gap-2">
              {uploadFiles.map((file, index) => (
                <div key={index} className="relative">
                  {typeof file === "string" ? (
                    // Display image from URL (backend file path)
                    <img
                      src={file}
                      alt={`preview-${index}`}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  ) : (
                    // Display image from File object (new upload)
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`preview-${index}`}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  )}
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                    style={{ transform: "translate(50%, -50%)" }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {/*Submitbuuton*/}
            <button
              type="submit"
              className={`bg-purple-700 w-full mt-5 h-10 rounded-lg text-white ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostForm;
